/* eslint-disable react/no-unescaped-entities, react-hooks/set-state-in-effect */
/**
 * AuthMagic — consuma un magic-link token e logga l'utente.
 *
 * iter v30 r140 — Reader App iOS smorzatore UX:
 *   1. L'agente atterra qui via Universal Link (prontoimmo.it/auth/magic?token=…)
 *   2. Chiamiamo POST /api/auth/magic-link/consume
 *   3. Salviamo JWT + user e portiamo in dashboard
 *   4. Su iOS l'app si è già aperta da sé (universal link). Sul desktop/Safari
 *      mobile l'utente continua sul web.
 *
 * Stati:
 *   - "loading"   → spinner mentre verifichiamo il token
 *   - "success"   → "Ti sto entrando…" 200ms transition prima della nav
 *   - "expired"   → token scaduto / consumato / invalido
 *   - "no-token"  → URL aperto senza ?token=
 */
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, apiError, saveJwt } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ProntoLogo from "@/components/ProntoLogo";
import { ArrowRight, ShieldCheck, EnvelopeSimple } from "@phosphor-icons/react";

export default function AuthMagic() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState("loading"); // loading | success | expired | no-token
  const [errorMsg, setErrorMsg] = useState("");

  const token = params.get("token");

  useEffect(() => {
    if (!token) {
      setState("no-token");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/auth/magic-link/consume", { token });
        if (cancelled) return;
        if (data?.token) {
          saveJwt(data.token);
        }
        if (data?.user) {
          setUser(data.user);
        }
        setState("success");
        setTimeout(() => {
          if (!cancelled) nav("/dashboard", { replace: true });
        }, 400);
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(apiError(err, "Link non valido o scaduto"));
        setState("expired");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, nav, setUser]);

  return (
    <div className="min-h-[100dvh] flex flex-col bg-gradient-to-b from-[#FAFAF7] to-[#F4F1EA]">
      <header className="px-6 pt-6 flex items-center justify-between">
        <ProntoLogo className="h-7" />
      </header>

      <main className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md text-center" data-testid="auth-magic-screen">
          {state === "loading" && (
            <div data-testid="auth-magic-loading">
              <div className="inline-flex w-14 h-14 rounded-full bg-[#7C8B6E]/15 items-center justify-center mb-5">
                <span
                  className="inline-block w-7 h-7 rounded-full border-[3px] border-[#7C8B6E] border-t-transparent animate-spin"
                  aria-hidden
                />
              </div>
              <h1 className="text-[22px] font-semibold text-[#1C1C1A] mb-2">
                Sto verificando il link…
              </h1>
              <p className="text-[14px] text-[#6B6A65] leading-relaxed">
                Un attimo solo. Ti faccio entrare nell'app.
              </p>
            </div>
          )}

          {state === "success" && (
            <div data-testid="auth-magic-success">
              <div className="inline-flex w-14 h-14 rounded-full bg-[#7C8B6E]/15 items-center justify-center mb-5">
                <ArrowRight size={26} weight="duotone" className="text-[#7C8B6E]" />
              </div>
              <h1 className="text-[22px] font-semibold text-[#1C1C1A] mb-2">
                Bentornato.
              </h1>
              <p className="text-[14px] text-[#6B6A65] leading-relaxed">
                Ti porto in dashboard.
              </p>
            </div>
          )}

          {state === "expired" && (
            <div data-testid="auth-magic-expired">
              <div className="inline-flex w-14 h-14 rounded-full bg-[#F0EAD8] items-center justify-center mb-5">
                <EnvelopeSimple size={26} weight="duotone" className="text-[#C9A875]" />
              </div>
              <h1 className="text-[22px] font-semibold text-[#1C1C1A] mb-2">
                Link non più valido.
              </h1>
              <p className="text-[14px] text-[#6B6A65] leading-relaxed mb-7">
                {errorMsg ||
                  "Il link è scaduto o è già stato usato. Chiedine uno nuovo dalla schermata di accesso."}
              </p>
              <Link
                to="/login"
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-[#7C8B6E] text-white text-[15px] font-semibold hover:bg-[#6E7C61] transition-colors"
                data-testid="auth-magic-back-login"
              >
                Torna ad accedere
                <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          )}

          {state === "no-token" && (
            <div data-testid="auth-magic-no-token">
              <div className="inline-flex w-14 h-14 rounded-full bg-[#F0EAD8] items-center justify-center mb-5">
                <EnvelopeSimple size={26} weight="duotone" className="text-[#C9A875]" />
              </div>
              <h1 className="text-[22px] font-semibold text-[#1C1C1A] mb-2">
                Manca il link.
              </h1>
              <p className="text-[14px] text-[#6B6A65] leading-relaxed mb-7">
                Apri il link che hai ricevuto via email per accedere. Se non l'hai
                ricevuto, chiedine uno nuovo dalla schermata di accesso.
              </p>
              <Link
                to="/login"
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-[#7C8B6E] text-white text-[15px] font-semibold hover:bg-[#6E7C61] transition-colors"
                data-testid="auth-magic-go-login"
              >
                Vai ad accedere
                <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          )}

          <div className="mt-10 pt-5 border-t border-[#E5E7E0] flex items-center justify-center gap-1.5 text-[10.5px] text-[#9A968B]">
            <ShieldCheck size={12} weight="duotone" className="text-[#7C8B6E]" />
            <span>Connessione cifrata · Dati in Europa · GDPR</span>
          </div>
        </div>
      </main>
    </div>
  );
}
